<template>
  <button
    ref="buttonElement"
    :class="{
      'tw-flex tw-items-center tw-justify-center tw-font-medium focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-deep-orange-600': true,
      'dark:bg-white tw-bg-black tw-text-white hover:tw-bg-deep-orange-600 dark:tw-text-black dark:tw-ring-offset-black dark:hover:tw-text-white':
        variant === 'filled' && !disabled,
      'tw-border tw-text-black tw-outline-none hover:tw-border-deep-orange-50 hover:tw-bg-deep-orange-50 hover:tw-text-deep-orange-600 dark:tw-border-slate-800 dark:tw-text-white':
        variant === 'outline' && !disabled,
      'tw-border-transparent tw-text-black hover:tw-text-deep-orange-600 focus:tw-ring-deep-orange-600 dark:tw-text-white dark:hover:tw-text-deep-orange-500':
        variant === 'text',
      'tw-border tw-border-rose-200 tw-text-rose-500 tw-outline-none hover:tw-border-rose-50 hover:tw-bg-rose-50 hover:tw-text-rose-600 focus:tw-ring-rose-500 dark:tw-border-rose-900 dark:tw-text-rose-200':
        variant === 'warn' && !disabled,
      '!tw-cursor-not-allowed tw-border tw-border-transparent !tw-bg-slate-200 !tw-text-slate-400 dark:!tw-bg-slate-400 dark:!tw-text-slate-200':
        disabled,
      'hover:tw-border-inherit hover:tw-bg-inherit hover:tw-text-inherit dark:hover:tw-border-inherit dark:hover:tw-bg-inherit dark:hover:tw-text-inherit':
        loading,
      'tw-h-8 tw-rounded-lg tw-px-3 tw-py-2 tw-text-xs': size === 'base',
      'tw-h-6 tw-rounded-md tw-px-2 tw-text-2xs': size === 'small'
    }"
    :disabled="disabled || loading"
    :type="type"
    :data-testid="trackingId"
    :data-disabled-reason="disabled && disabledReason ? disabledReason : undefined"
    @mouseenter="showTooltip = true"
    @mouseleave="showTooltip = false"
    @focus="showTooltip = true"
    @blur="showTooltip = false"
  >
    <div
      v-if="$slots.icon || loading"
      :class="{
        'tw-flex tw-items-center tw-justify-center': true,
        '-tw-mx-[5px]': size === 'small',
        '-tw-mx-[7px]': size === 'base',
        '-tw-ml-1 tw-mr-1': $slots.default,
        'tw-h-4 tw-w-4': size === 'small',
        'tw-h-5 tw-w-5': size === 'base'
      }"
    >
      <TablerLoader2 v-if="loading" class="tw-animate-spin" />
      <slot v-else name="icon" />
    </div>
    <slot v-if="$slots.default" />
    <TablerChevronDown v-if="dropdown" class="-tw-mr-1 tw-ml-1 tw-h-3 tw-w-3" />

    <StudioTooltip :show="showTooltip" :text="disabledReason" :targetElement="$refs.buttonElement as Element" />
  </button>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';

import TablerChevronDown from './Icons/tabler/TablerChevronDown.vue';
import TablerLoader2 from './Icons/tabler/TablerLoader2.vue';
import StudioTooltip from './StudioTooltip.vue';

export type StudioButtonVariant = 'filled' | 'outline' | 'text' | 'warn';

export default defineComponent({
  name: 'StudioButton',
  components: { TablerLoader2, TablerChevronDown, StudioTooltip },
  props: {
    variant: {
      type: String as () => StudioButtonVariant,
      default: 'filled',
      validator: (value: string) => ['filled', 'outline', 'text', 'warn'].includes(value)
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dropdown: {
      type: Boolean,
      default: false
    },
    type: {
      type: String as () => 'submit' | 'reset' | 'button',
      default: 'button',
      validator: (value: string) => ['submit', 'reset', 'button'].includes(value)
    },
    size: {
      type: String as () => 'base' | 'small',
      default: 'base',
      validator: (value: string) => ['base', 'small'].includes(value)
    },
    disabledReason: {
      type: String,
      default: ''
    },
    trackingId: {
      type: String,
      required: true
    }
  },
  setup() {
    const showTooltip = ref(false);

    return {
      showTooltip
    };
  }
});
</script>
